import React from 'react';
import { Receipt as ReceiptIcon } from 'lucide-react';

/**
 * Gate Fee button — sits next to Fix Plate on every surface that offers it.
 *
 * 在派单列表里，它只出现在「Awaiting Gate Fee」那一段的行上（2026-09-09）。
 * 原来是每张进口任务都挂一个，理由是「没有可靠的『这只柜欠费』信号」——那个
 * 理由现在不成立了：三家码头各自的判据能算出「只差这一笔就能放行」，按钮就该
 * 跟着信号走。到处都摆一个，等于让调度员一行行去点着问。
 *
 * 任务详情里的那个入口保持原样：那是人主动打开这张任务之后的手动查询，判据
 * 漏掉的情况（比如 TraPac 侧至今没有真实样本）还得靠它兜底。
 *
 * 'paid' is the one state that carries information: we hold a receipt for this
 * container. The action stays available (a second fee can genuinely come due)
 * but it reads as done rather than pending.
 */
export type GateFeeState = 'idle' | 'paid';

/**
 * 绿 = 还没付，去付；灰 = 已经付过了。
 *
 * 颜色的方向是「还要不要动手」，不是「事情好不好」。图标只出现在待办的柜子上，
 * 所以默认那个状态就该是显眼的那一个；付完之后它退成灰色，留着但不再喊人。
 */
const STYLES: Record<GateFeeState, { className: string; title: string; label: string }> = {
  idle: {
    className: 'border-green-300 text-green-600 hover:bg-green-50 hover:border-green-400',
    title: 'Look up and pay this container\'s terminal gate fee on eModal',
    label: 'Pay gate fee on eModal',
  },
  paid: {
    className: 'border-gray-200 text-gray-400 hover:bg-gray-50 hover:border-gray-300',
    title: 'Gate fee already paid — click to check eModal again',
    label: 'Gate fee paid',
  },
};

export const GateFeeButton: React.FC<{
  state?: GateFeeState;
  onClick: () => void;
  /** Stop the click from also triggering row-level handlers (open detail, drag). */
  stopPropagation?: boolean;
}> = ({ state = 'idle', onClick, stopPropagation }) => {
  const style = STYLES[state] || STYLES.idle;
  return (
    <button
      type="button"
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
        onClick();
      }}
      title={style.title}
      aria-label={style.label}
      className={
        'inline-flex items-center justify-center h-7 w-7 rounded-md border transition-colors '
        + style.className
      }
    >
      <ReceiptIcon className="w-3.5 h-3.5" />
    </button>
  );
};

export default GateFeeButton;
